(() => {
  if (customElements.get('subscription-option')) return;

  class SubscriptionOption extends HTMLElement {
    constructor() {
      super();
      this.onPurchaseTypeChange = this.onPurchaseTypeChange.bind(this);
      this.onCustomSelectTriggerClick = this.onCustomSelectTriggerClick.bind(this);
      this.onCustomSelectOptionClick = this.onCustomSelectOptionClick.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
    }

    connectedCallback() {
      this.purchaseTypeInputs = Array.from(this.querySelectorAll('.js-subscription-option-purchase-type'));
      this.customSelect = this.querySelector('.js-subscription-option-schedule');
      this.sellingPlanInput = this.querySelector('.js-subscription-option-selling-plan');
      this.subscribeItem = this.querySelector('.subscription-option__purchase-option-item--subscribe');
      this.onetimeItem = this.querySelector('.subscription-option__purchase-option-item--onetime');
      this.subscribeCurrentPrice = this.subscribeItem?.querySelector('.subscription-option__purchase-option-price--current');
      this.subscribeOriginalPrice = this.subscribeItem?.querySelector('.subscription-option__purchase-option-price--original s');
      this.onetimeCurrentPrice = this.onetimeItem?.querySelector('.subscription-option__purchase-option-price--current');
      this.discountEl = this.querySelector('.js-subscription-option-discount');

      this.purchaseTypeInputs.forEach((input) => {
        input.addEventListener('change', this.onPurchaseTypeChange);
      });

      if (this.customSelect) {
        this.customSelect
          .querySelector('.subscription-option__custom-select-trigger')
          ?.addEventListener('click', this.onCustomSelectTriggerClick);
        this.customSelect
          .querySelectorAll('.subscription-option__custom-select-option')
          .forEach((opt) => opt.addEventListener('click', this.onCustomSelectOptionClick));
        document.addEventListener('click', this.onDocumentClick);
      }

      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      requestAnimationFrame(() => this.initState());
    }

    initState() {
      const isSubscribe = this.querySelector('.js-subscription-option-purchase-type:checked')?.value === 'subscribe';
      if (!isSubscribe) return;

      if (this.sellingPlanInput && !this.sellingPlanInput.value && this.customSelect?.dataset.value) {
        this.sellingPlanInput.value = this.customSelect.dataset.value;
        this.sellingPlanInput.disabled = false;
      }

      const resolved = this.resolveCurrentPrices();
      if (resolved) this.updateWidgetPriceDisplay(resolved.subPrice, resolved.originalPrice);

      this.updateProductPriceBlock(true);

      if (typeof getPriceByVariantAndPlan === 'function') {
        getPriceByVariantAndPlan();
      }
    }

    disconnectedCallback() {
      this.purchaseTypeInputs?.forEach((input) => {
        input.removeEventListener('change', this.onPurchaseTypeChange);
      });
      if (this.customSelect) {
        this.customSelect
          .querySelector('.subscription-option__custom-select-trigger')
          ?.removeEventListener('click', this.onCustomSelectTriggerClick);
        this.customSelect
          .querySelectorAll('.subscription-option__custom-select-option')
          .forEach((opt) => opt.removeEventListener('click', this.onCustomSelectOptionClick));
        document.removeEventListener('click', this.onDocumentClick);
      }
      this.variantChangeUnsubscriber?.();
    }

    updateURL(planId) {
      if (this.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      if (planId) {
        url.searchParams.set('selling_plan', planId);
      } else {
        url.searchParams.delete('selling_plan');
      }
      history.replaceState(history.state, '', url.toString());
    }

    onPurchaseTypeChange(event) {
      const isSubscribe = event.target.value === 'subscribe';
      this.applyPurchaseType(isSubscribe);
      this.updateURL(isSubscribe ? this.customSelect?.dataset.value : null);
    }

    applyPurchaseType(isSubscribe) {
      this.subscribeItem.classList.toggle('is-selected', isSubscribe);
      this.onetimeItem.classList.toggle('is-selected', !isSubscribe);

      if (this.customSelect) {
        if (isSubscribe) {
          this.customSelect.removeAttribute('data-disabled');
        } else {
          this.customSelect.setAttribute('data-disabled', 'true');
          this.customSelect.classList.remove('is-open');
          this.customSelect
            .querySelector('.subscription-option__custom-select-trigger')
            ?.setAttribute('aria-expanded', 'false');
        }
      }

      if (this.sellingPlanInput) {
        if (isSubscribe) {
          this.sellingPlanInput.value = this.customSelect?.dataset.value || '';
          this.sellingPlanInput.disabled = false;
        } else {
          this.sellingPlanInput.value = '';
          this.sellingPlanInput.disabled = true;
        }
      }

      this.updateProductPriceBlock(isSubscribe);
      this.updateVariantPrices();

      if (typeof getPriceByVariantAndPlan === 'function') {
        getPriceByVariantAndPlan();
      }
    }

    onCustomSelectTriggerClick(event) {
      event.stopPropagation();
      if (this.customSelect.hasAttribute('data-disabled')) return;
      const isOpen = this.customSelect.classList.toggle('is-open');
      this.customSelect
        .querySelector('.subscription-option__custom-select-trigger')
        ?.setAttribute('aria-expanded', String(isOpen));
    }

    onCustomSelectOptionClick(event) {
      this.selectCustomOption(event.currentTarget, true);
    }

    selectCustomOption(optionEl, doSave) {
      const value = optionEl.dataset.value;
      const text = optionEl.textContent.trim();

      this.customSelect.querySelectorAll('.subscription-option__custom-select-option').forEach((opt) => {
        const selected = opt === optionEl;
        opt.classList.toggle('is-selected', selected);
        opt.setAttribute('aria-selected', String(selected));
      });

      const valueEl = this.customSelect.querySelector('.subscription-option__custom-select-value');
      if (valueEl) valueEl.textContent = text;

      this.customSelect.dataset.value = value;
      this.customSelect.classList.remove('is-open');
      this.customSelect
        .querySelector('.subscription-option__custom-select-trigger')
        ?.setAttribute('aria-expanded', 'false');

      this.sellingPlanInput.value = value;

      if (doSave) {
        this.updateURL(value);
        const resolved = this.resolveCurrentPrices();
        if (resolved) this.updateWidgetPriceDisplay(resolved.subPrice, resolved.originalPrice);
        this.updateProductPriceBlock(true);
        if (typeof getPriceByVariantAndPlan === 'function') {
          getPriceByVariantAndPlan();
        }
      }
    }

    onDocumentClick(event) {
      if (!this.customSelect?.contains(event.target)) {
        this.customSelect?.classList.remove('is-open');
        this.customSelect
          ?.querySelector('.subscription-option__custom-select-trigger')
          ?.setAttribute('aria-expanded', 'false');
      }
    }

    onVariantChange(event) {
      if (event.data.sectionId !== this.dataset.sectionId) return;
      const variant = event.data.variant;
      if (!variant) return;
      this.updateVariantPrices(variant);
      const isSubscribe = this.querySelector('.js-subscription-option-purchase-type:checked')?.value === 'subscribe';
      if (isSubscribe) this.updateURL(this.customSelect?.dataset.value || null);
    }

    updateVariantPrices(variant) {
      if (!variant) {
        const resolved = this.resolveCurrentPrices();
        if (resolved) {
          this.updateWidgetPriceDisplay(resolved.subPrice, resolved.originalPrice);
          this.updatePerNightBadge(resolved.subPrice, resolved.originalPrice);
        }
        return;
      }

      const currentPlanId = this.customSelect?.dataset.value || null;
      const discountPercent = parseFloat(this.dataset.discountPercent || 0);
      const isSubscribe = this.querySelector('.js-subscription-option-purchase-type:checked')?.value === 'subscribe';

      let subPrice = null;
      let originalPrice = variant.price;

      if (currentPlanId && Array.isArray(variant.selling_plan_allocations)) {
        const allocation = variant.selling_plan_allocations.find(
          (a) => String(a.selling_plan_id) === String(currentPlanId)
        );
        if (allocation) {
          subPrice = allocation.price;
          originalPrice = allocation.compare_at_price || variant.price;
        }
      }

      if (subPrice === null && discountPercent > 0) {
        subPrice = Math.round(variant.price * (1 - discountPercent / 100));
      }

      this.updateWidgetPriceDisplay(subPrice, originalPrice);
      this.resetPriceBlockCache();
      if (isSubscribe) this.updateProductPriceBlock(true, subPrice, originalPrice);
    }

    updateWidgetPriceDisplay(subPrice, originalPrice) {
      if (subPrice !== null && this.subscribeCurrentPrice) {
        this.subscribeCurrentPrice.textContent = this.formatMoney(subPrice);
      }
      if (this.subscribeOriginalPrice) {
        this.subscribeOriginalPrice.textContent = this.formatMoney(originalPrice);
      }
      if (this.onetimeCurrentPrice) {
        this.onetimeCurrentPrice.textContent = this.formatMoney(originalPrice);
      }
      if (subPrice !== null) this.updateDiscountDisplay(subPrice, originalPrice);
      this.updatePerNightBadge(subPrice, originalPrice);
      this.updatePriceDiffBadge(subPrice, originalPrice);
    }

    updatePriceDiffBadge(subPrice, originalPrice) {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;
      
      const badge = priceBlock.querySelector('.price-diff-badge');
      if (!badge) return;

      const isSubscribe = this.purchaseTypeInputs.find(i => i.checked)?.value === 'subscribe';
      const activePrice = isSubscribe ? subPrice : originalPrice;
      
      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;
      
      let topComparePrice = originalPrice;
      if (variantDataEl && variantId) {
        try {
          const variants = JSON.parse(variantDataEl.textContent);
          const variant = variants.find(v => String(v.id) === String(variantId));
          if (variant) {
            // Priority: variant.compare_at_price > variant.price (originalPrice)
            topComparePrice = variant.compare_at_price || variant.price;
          }
        } catch (e) {}
      }

      const diff = Math.max(0, topComparePrice - activePrice);
      if (diff > 0) {
        const wholeDollars = Math.round(diff / 100) * 100;
        const formatted = this.formatMoney(wholeDollars).replace(/[.,]00(?=[^0-9]|$)/, '');
        badge.textContent = `${formatted} OFF`;
        badge.style.display = 'inline-block';
      } else {
        badge.style.display = 'none';
      }
    }

    updatePerNightBadge(subPrice, originalPrice) {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;
      
      const badge = priceBlock.querySelector('.pernight-bedge:not(.price-diff-badge)');
      if (!badge) return;

      const isSubscribe = this.purchaseTypeInputs.find(i => i.checked)?.value === 'subscribe';
      const activePrice = isSubscribe ? subPrice : originalPrice;

      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;
      
      let optionDays = this.resolveNightsPerUnit(priceBlock, variantId, variantDataEl);
      const perNight = activePrice / optionDays;
      const formatted = this.formatMoney(perNight);
      
      const currentText = badge.textContent.trim();
      const textSuffix = currentText.replace(/[0-9.,$£€₸\s]+/g, '').trim();
      
      badge.textContent = `${formatted} ${textSuffix}`;
    }

    resolveNightsPerUnit(priceBlock, variantId, variantDataEl) {
      const priceEl = priceBlock?.querySelector?.('.price') || priceBlock;
      const raw = priceEl?.dataset?.variantNights;
      if (raw && variantId) {
        try {
          const nightsMap = JSON.parse(raw);
          const nights = nightsMap[String(variantId)];
          if (nights != null && Number(nights) > 0) return Number(nights);
        } catch (e) {}
      }

      let optionDays = 30;
      if (variantDataEl && variantId) {
        try {
          const variants = JSON.parse(variantDataEl.textContent);
          const variant = variants.find(v => String(v.id) === String(variantId));
          if (variant && variant.options) {
            variant.options.forEach(opt => {
              const val = String(opt).toLowerCase();
              if (val.includes('2-pack')) optionDays = 60;
              else if (val.includes('3-pack')) optionDays = 90;
              else if (val.includes('4-pack')) optionDays = 120;
            });
          }
        } catch (e) {}
      }
      return optionDays;
    }

    updateDiscountDisplay(subPrice, originalPrice) {
      if (!this.discountEl || this.dataset.showDiscount !== 'true') return;
      const diff = originalPrice - subPrice;
      if (diff <= 0) {
        this.discountEl.textContent = '';
        return;
      }
      if (this.dataset.discountType === 'amount') {
        const wholeDollars = Math.round(diff / 100) * 100;
        const formatted = this.formatMoney(wholeDollars).replace(/[.,]00(?=[^0-9]|$)/, '');
        this.discountEl.textContent = `Save ${formatted}`;
      } else {
        const pct = Math.round((diff / originalPrice) * 100);
        this.discountEl.textContent = `Save ${pct}%`;
      }
    }

    resolveCurrentPrices() {
      const currentPlanId = this.customSelect?.dataset.value || null;
      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;
      if (!variantDataEl || !variantId) return null;

      try {
        const variants = JSON.parse(variantDataEl.textContent);
        const variant = variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return null;

        if (currentPlanId && Array.isArray(variant.selling_plan_allocations)) {
          const allocation = variant.selling_plan_allocations.find(
            (a) => String(a.selling_plan_id) === String(currentPlanId)
          );
          if (allocation) {
            return { subPrice: allocation.price, originalPrice: allocation.compare_at_price || variant.price };
          }
        }

        const discountPercent = parseFloat(this.dataset.discountPercent || 0);
        return {
          subPrice: discountPercent > 0 ? Math.round(variant.price * (1 - discountPercent / 100)) : variant.price,
          originalPrice: variant.price,
        };
      } catch (e) {
        return null;
      }
    }

    resetPriceBlockCache() {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;
      const priceEl = priceBlock.querySelector('.price');
      if (!priceEl) return;
      priceEl.classList.remove('subscription-active');
      delete priceEl.dataset.originalOnSale;
      priceEl
        .querySelectorAll('[data-original]')
        .forEach((el) => el.removeAttribute('data-original'));
    }

    updateProductPriceBlock(isSubscribe, subPrice, originalPrice) {
      const priceBlock = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceBlock) return;
      const priceEl = priceBlock.querySelector('.price');
      if (!priceEl) return;

      const saleEl = priceEl.querySelector('.price__sale');
      const saleItem = saleEl?.querySelector('.price-item--sale');
      let compareItem = saleEl?.querySelector('s.price-item--regular, .price-item--regular:not(.price-item--sale)');
      const regularItem = priceEl.querySelector('.price__regular .price-item--regular');

      if (!isSubscribe) {
        if (priceEl.classList.contains('subscription-active')) {
          priceEl.classList.remove('subscription-active');
          delete priceEl.dataset.originalOnSale;

          const regular = this.resolveVariantRegularPrices();
          if (regular) {
            const isOnSale = regular.compareAtPrice && regular.compareAtPrice > regular.price;
            priceEl.classList.toggle('price--on-sale', !!isOnSale);
            if (saleItem) saleItem.textContent = this.formatMoney(regular.price);
            if (compareItem) compareItem.textContent = isOnSale ? this.formatMoney(regular.compareAtPrice) : '';
            if (regularItem) regularItem.textContent = this.formatMoney(regular.price);
          } else {
            const hadOnSale = priceEl.dataset.originalOnSale === '1';
            priceEl.classList.toggle('price--on-sale', hadOnSale);
            if (saleItem?.dataset.original !== undefined) saleItem.textContent = saleItem.dataset.original;
            if (compareItem?.dataset.original !== undefined) compareItem.textContent = compareItem.dataset.original;
            if (regularItem?.dataset.original !== undefined) regularItem.textContent = regularItem.dataset.original;
          }
        }
        return;
      }

      if (subPrice === undefined) {
        const resolved = this.resolveCurrentPrices();
        if (!resolved) return;
        subPrice = resolved.subPrice;
        originalPrice = resolved.originalPrice;
      }

      if (!priceEl.classList.contains('subscription-active')) {
        if (saleItem) saleItem.dataset.original = saleItem.textContent.trim();
        if (compareItem) compareItem.dataset.original = compareItem.textContent.trim();
        if (regularItem) regularItem.dataset.original = regularItem.textContent.trim();
        priceEl.dataset.originalOnSale = priceEl.classList.contains('price--on-sale') ? '1' : '0';
        priceEl.classList.add('subscription-active');
      }

      // For featured product only: create compare element if not in DOM
      if (this.dataset.updateUrl === 'false' && !compareItem && saleEl && saleItem) {
        const wrapper = document.createElement('span');
        const s = document.createElement('s');
        s.className = 'price-item price-item--regular';
        wrapper.appendChild(s);
        saleEl.insertBefore(wrapper, saleItem);
        compareItem = s;
      }

      priceEl.classList.add('price--on-sale');
      if (saleItem) saleItem.textContent = this.formatMoney(subPrice);
      if (compareItem) compareItem.textContent = this.formatMoney(originalPrice);
    }

    resolveVariantRegularPrices() {
      const variantDataEl = document.querySelector(
        `variant-radios[data-section="${this.dataset.sectionId}"] script[type="application/json"],
         variant-selects[data-section="${this.dataset.sectionId}"] script[type="application/json"]`
      );
      const variantId = document.querySelector(`#product-form-${this.dataset.sectionId} .product-variant-id`)?.value;
      if (!variantDataEl || !variantId) return null;
      try {
        const variants = JSON.parse(variantDataEl.textContent);
        const variant = variants.find((v) => String(v.id) === String(variantId));
        if (!variant) return null;
        return { price: variant.price, compareAtPrice: variant.compare_at_price };
      } catch (e) {
        return null;
      }
    }

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.Shopify.moneyFormat || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }
  }

  customElements.define('subscription-option', SubscriptionOption);
})();
